import React, { useState } from 'react';
import { useAuth, VERSION_INFO } from '../contexts/AuthContext';
import { EnvelopeIcon, PhoneIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import WorkspaceSelector from '../components/auth/WorkspaceSelector';

const Login: React.FC = () => {
    const { requestLoginOtp, verifyLoginOtp, selectWorkspace } = useAuth();

    // State
    const [identifier, setIdentifier] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [otpStep, setOtpStep] = useState(false);
    const [otp, setOtp] = useState('');
    const [challengeToken, setChallengeToken] = useState('');
    const [identifierHint, setIdentifierHint] = useState('');
    const [info, setInfo] = useState('');

    // Multi-tenant workspace selection state
    const [workspaceStep, setWorkspaceStep] = useState(false);
    const [workspaceToken, setWorkspaceToken] = useState('');
    const [memberships, setMemberships] = useState<any[]>([]);

    // Handle OTP request (passwordless login)
    const handleRequestOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await requestLoginOtp(identifier);
            setChallengeToken(result.challengeToken || '');
            setIdentifierHint(result.identifierHint || identifier);
            setInfo(result.message || 'Verification code sent.');
            setOtpStep(true);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await verifyLoginOtp(challengeToken, otp);

            // Check if workspace selection is required
            if (result?.requiresWorkspaceSelection) {
                setWorkspaceToken(result.workspaceToken);
                setMemberships(result.memberships);
                setWorkspaceStep(true);
                setOtpStep(false);
                return;
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleWorkspaceSelect = async (tenantId: number) => {
        setError('');
        setIsLoading(true);
        try {
            await selectWorkspace(workspaceToken, tenantId);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const resetLogin = () => {
        setOtpStep(false);
        setWorkspaceStep(false);
        setOtp('');
        setChallengeToken('');
        setIdentifierHint('');
        setInfo('');
        setWorkspaceToken('');
        setMemberships([]);
        setError('');
    };

    const isEmail = identifier.includes('@');

    return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute top-1/2 -left-40 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-pulse delay-500"></div>
            </div>

            {/* Main Content */}
            <div className="relative min-h-screen flex items-center justify-center p-4">
                {/* Workspace Selector (Multi-tenant) */}
                {workspaceStep ? (
                    <WorkspaceSelector
                        memberships={memberships}
                        onSelect={handleWorkspaceSelect}
                        loading={isLoading}
                    />
                ) : (
                <div className="w-full max-w-md">
                    {/* Glass Card */}
                    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 sm:p-10 border border-white/20">
                        {/* Logo/Header Section */}
                        <div className="text-center mb-8">
                            {/* Animated Logo */}
                            <div className="inline-flex items-center justify-center mb-6 relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-500 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-xl transform hover:scale-110 transition-transform duration-300">
                                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                            </div>

                            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                                eSanza
                            </h1>
                            <p className="text-gray-600 text-sm sm:text-base font-medium">
                                Dashboard Access Portal
                            </p>
                            <div className="mt-2 inline-block px-4 py-1 bg-emerald-50 rounded-full">
                                <p className="text-xs text-emerald-700 font-semibold">Authorized Staff Only</p>
                            </div>
                        </div>

                        {/* OTP Step */}
                        {otpStep && (
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 mb-5">
                                <div className="flex items-start gap-3">
                                    <ShieldCheckIcon className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                                    <div>
                                        <p className="text-sm font-bold text-emerald-900">Email verification</p>
                                        <p className="mt-1 text-xs leading-5 text-emerald-700">
                                            {info || 'Enter the code sent to your email.'} {identifierHint && `Sent to ${identifierHint}.`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Login Form */}
                        <form onSubmit={otpStep ? handleOtpVerify : handleRequestOtp} className="space-y-5">
                            {/* Identifier Input (Email or Phone) */}
                            <div className={otpStep ? 'opacity-60' : ''}>
                                <label htmlFor="identifier" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email or Phone Number
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        {!isEmail && identifier.length > 0 ? (
                                            <PhoneIcon className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                                        ) : (
                                            <EnvelopeIcon className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                                        )}
                                    </div>
                                    <input
                                        id="identifier"
                                        type="text"
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        placeholder="your.email@example.com or +250..."
                                        required
                                        disabled={otpStep}
                                        className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all duration-200 outline-none text-gray-900 placeholder-gray-400"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {/* OTP Input */}
                            {otpStep && (
                                <div>
                                    <label htmlFor="otp" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Verification Code
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <ShieldCheckIcon className="h-5 w-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                                        </div>
                                        <input
                                            id="otp"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            pattern="[0-9]{6}"
                                            maxLength={6}
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="000000"
                                            required
                                            autoFocus
                                            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all duration-200 outline-none text-center text-2xl font-black tracking-[0.35em] text-gray-900 placeholder-gray-300"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={resetLogin}
                                        className="mt-3 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
                                    >
                                        Use a different account
                                    </button>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 animate-shake">
                                    <div className="flex items-center">
                                        <svg className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm font-medium text-red-800">{error}</p>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="group relative w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 disabled:from-gray-300 disabled:to-gray-400 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:scale-100 shadow-lg hover:shadow-xl disabled:cursor-not-allowed overflow-hidden"
                            >
                                {/* Button Glow Effect */}
                                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>

                                <span className="relative flex items-center justify-center">
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {otpStep ? 'Verifying...' : 'Sending code...'}
                                        </>
                                    ) : (
                                        <>
                                            {otpStep ? 'Verify & Enter Dashboard' : 'Send Verification Code'}
                                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                            </svg>
                                        </>
                                    )}
                                </span>
                            </button>
                        </form>

                        {/* Footer */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span className="font-medium">Secure Login • SSL Encrypted</span>
                            </div>
                            <p className="text-center text-xs text-gray-400 mt-3">
                                © 2026 eSanza. All rights reserved.
                            </p>
                            <p className="text-center text-xs text-gray-400 mt-1">
                                v{VERSION_INFO.backend_version || VERSION_INFO.frontend_version || '1.0.0'}
                                {VERSION_INFO.git_commit && VERSION_INFO.git_commit !== 'unknown' ? ` • ${VERSION_INFO.git_commit}` : ''}
                                {VERSION_INFO.environment && VERSION_INFO.environment !== 'production' ? ` • ${VERSION_INFO.environment}` : ''}
                            </p>
                        </div>
                    </div>

                    {/* Help Text */}
                    <div className="mt-6 text-center">
                        <p className="text-white/90 text-sm font-medium drop-shadow-lg">
                            Need access? Contact the platform owner or project manager
                        </p>
                    </div>
                </div>
                )}
            </div>

            {/* Add custom animation for shake */}
            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                    20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
                .animate-shake {
                    animation: shake 0.5s;
                }
            `}</style>
        </div>
    );
};

export default Login;
